/**
 * LanguageSelector - Multilingual dropdown for The Wire Report
 * Supports English, Spanish, and Portuguese
 */

import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
];

const LanguageSelector = ({ currentLanguage = 'en', onLanguageChange, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = languages.find((l) => l.code === currentLanguage) || languages[0];

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSelect = (code) => {
    onLanguageChange?.(code);
    setIsOpen(false);
  };

  if (compact) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-1.5 bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.12] rounded-lg transition-colors"
          aria-label="Select language"
        >
          <span className="text-base">{currentLang.flag}</span>
          <ChevronDown className={`w-3 h-3 text-white/60 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full right-0 mt-1 py-1 bg-black/95 backdrop-blur-xl border border-white/[0.12] rounded-lg shadow-xl min-w-[120px] z-50">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/[0.08] transition-colors ${
                  lang.code === currentLanguage ? 'bg-white/[0.06]' : ''
                }`}
              >
                <span className="text-base">{lang.flag}</span>
                <span className="text-white text-sm flex-1">{lang.name}</span>
                {lang.code === currentLanguage && (
                  <Check className="w-4 h-4 text-yellow-400" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.12] rounded-lg transition-colors"
        aria-label="Select language"
      >
        <Globe className="w-4 h-4 text-white/60" />
        <span className="text-base">{currentLang.flag}</span>
        <span className="text-white text-sm">{currentLang.name}</span>
        <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 py-1 bg-black/95 backdrop-blur-xl border border-white/[0.12] rounded-lg shadow-xl min-w-[160px] z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/[0.08] transition-colors ${
                lang.code === currentLanguage ? 'bg-white/[0.06]' : ''
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="text-white text-sm flex-1">{lang.name}</span>
              {lang.code === currentLanguage && (
                <Check className="w-4 h-4 text-yellow-400" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
